import { useAuthStore } from '@/stores/authStore';
import { useAccountStore } from '@/stores/accountStore';
import { polyAccountReadScope } from '@/lib/polymarket/accountScope';
import type { PolyMergeAccounting } from '@/lib/polymarket/mergeAccounting';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  listPolyRealizedViaRelay,
  type PolyRelayRealizedBucket,
  type PolyRelayUnavailableCode,
  type PolyReadCredentialContext,
} from '@/lib/polymarket/relay';
import { startVisibilityAwareInterval } from '@/lib/visibilityThrottle';

export type PolyRelayRealizedStatus = 'idle' | 'loading' | 'ready' | 'error' | 'unavailable';

export interface UsePolymarketRelayRealizedOptions {
  enabled?: boolean;
  pollMs?: number;
}

export interface UsePolymarketRelayRealizedReturn {
  buckets: PolyRelayRealizedBucket[];
  merges: PolyMergeAccounting[] | null;
  mergesUnavailable: boolean;
  status: PolyRelayRealizedStatus;
  error: string | null;
  unavailableCode: PolyRelayUnavailableCode | null;
  credentialContext: PolyReadCredentialContext;
  lastFetchMs: number | null;
  refresh: () => Promise<void>;
}

// Settled buckets move on settlement cadence (minutes), not per tick — and
// off the serve path each fetch is a lifetime-ticker CLOB scan + full fills
// fold on the relay. 10s polling replayed the Jul-2 read-fire pattern
// (Jul-14 audit); the relay also TTL-caches per user as a second layer.
const DEFAULT_POLL_MS = 120_000;

/** Fetch-on-mount + slow visibility-aware poll of the settlement-aware
 *  ledger used by Polymarket My Trades. `refresh()` is exposed for an
 *  explicit user-driven update. */
export function usePolymarketRelayRealized(
  options: UsePolymarketRelayRealizedOptions = {},
): UsePolymarketRelayRealizedReturn {
  const enabled = options.enabled ?? true;
  const pollMs = options.pollMs ?? DEFAULT_POLL_MS;
  useAccountStore(polyAccountReadScope);
  const identity = useAuthStore(polyAccountReadScope);
  type Reading = Omit<UsePolymarketRelayRealizedReturn, 'refresh'>;
  const empty: Reading = { buckets: [], merges: null, mergesUnavailable: false, status: 'idle',
    error: null, unavailableCode: null, credentialContext: null, lastFetchMs: null };
  const [reading, setReading] = useState<Reading & { identity: string }>({ ...empty, identity });
  const current = reading.identity === identity ? reading : empty;
  const inFlightRef = useRef<string | null>(null);
  const generationRef = useRef(0);
  const unavailableRef = useRef(false);

  const refresh = useCallback(async () => {
    if (inFlightRef.current === identity) return;
    inFlightRef.current = identity;
    const generation = ++generationRef.current;
    unavailableRef.current = false;
    setReading(prev => prev.identity === identity ? { ...prev, status: prev.status === 'idle' ? 'loading' : prev.status }
      : { identity, buckets: [], merges: null, mergesUnavailable: false, status: 'loading',
        error: null, unavailableCode: null, credentialContext: null, lastFetchMs: null });
    try {
      const out = await listPolyRealizedViaRelay();
      if (generation !== generationRef.current || polyAccountReadScope() !== identity) return;
      unavailableRef.current = !out.ok && !!out.code;
      setReading(prev => {
        if (out.ok) return { ...prev, identity, buckets: out.buckets,
          merges: out.merges ?? (prev.identity === identity ? prev.merges : null),
          mergesUnavailable: out.merges == null, error: null, unavailableCode: null,
          credentialContext: out.credentialContext === undefined ? 'CONTEXT_CHECK_FAILED' : out.credentialContext,
          status: 'ready', lastFetchMs: Date.now() };
        if (out.code) return { identity, buckets: [], merges: null, mergesUnavailable: true,
          error: out.error, unavailableCode: out.code, credentialContext: out.code,
          status: 'unavailable', lastFetchMs: Date.now() };
        return { ...prev, error: out.error, unavailableCode: null, mergesUnavailable: true, status: 'error', lastFetchMs: Date.now() };
      });
    } finally {
      if (generation === generationRef.current) inFlightRef.current = null;
    }
  }, [identity]);

  useEffect(() => {
    if (!enabled) return;
    let stopped = false;
    void refresh();
    const stopInterval = startVisibilityAwareInterval(() => {
      if (!stopped && !unavailableRef.current) void refresh();
    }, pollMs);
    return () => {
      stopped = true;
      stopInterval();
    };
  }, [enabled, pollMs, refresh]);

  return { ...current, refresh };
}
